import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";

import type { WorkspaceSummary } from "../lib/api";
import { AddWorkspaceModal } from "./AddWorkspaceModal";
import { useSystemStatus } from "../state/SystemStatusContext";
import {
  workspaceHealthColor,
  workspaceHealthReason,
  type HealthColor,
} from "../lib/workspaceHealth";
import { getWorkspaceInitials } from "../lib/workspaceInitials";
import { gradientCSSFor } from "../lib/avatarGradient";

interface WorkspaceSidebarProps {
  workspaces: WorkspaceSummary[];
  activeWorkspaceID: string | null;
  onSelect: (teamID: string) => void;
}

// Phase 5.0: per-color CSS class. The badge is rendered as an 8px
// circle pinned to the bottom-right of the workspace icon. Colors
// taken from Tailwind's named palette (not the theme system) so
// the badge reads consistently in both slack + records themes —
// a green dot must look green regardless of which theme owns the
// surrounding chrome.
const BADGE_COLOR_CLASS: Record<HealthColor, string> = {
  green: "bg-theme-success-text",
  yellow: "bg-theme-warning-text",
  red: "bg-theme-error-border",
  gray: "bg-theme-text-tertiary",
};

// Narrow column on the far left, theme-bg-sidebar (Aubergine in
// slack mode, stone-100 in records). Pass 1.6: rail click is
// reserved for workspace switching only — info panel opens via
// a dedicated affordance in the ConversationList header.
//
// Pass 2.2: workspaces are server-side now; we removed the
// per-tile remove (X) affordance because removing a workspace
// from the viewer would only desync from the service, not
// actually delete anything. Removal lives at the service tier.
//
// Pass 3.8 cutover: the dashed "+" tile that opened /upload is
// gone with the ZIP path. Workspaces are added by the operator
// running ``python -m slack_sync login`` + ``backfill`` outside
// the viewer; there is no in-viewer affordance to ingest.
//
// Phase 5.0: each icon now carries a small health dot in its
// bottom-right corner (green / yellow / red / gray, per
// workspaceHealthColor). Click → /status for the deep-dive
// diagnostics. The badge state is derived client-side from the
// ambient /system/status payload; no extra backend round-trip
// per workspace. Tooltip surfaces the underlying reason
// ("needs re-auth — token_revoked", "polling stale — last poll
// 47m ago", etc) so the operator sees what's actually wrong on
// hover without having to leave their current view.
export function WorkspaceSidebar({
  workspaces,
  activeWorkspaceID,
  onSelect,
}: WorkspaceSidebarProps) {
  const navigate = useNavigate();
  const { status: systemStatus } = useSystemStatus();
  const [showAdd, setShowAdd] = useState(false);

  // Index the per-workspace status by team_id so we can join in
  // O(1) per icon as we render. Falls back to undefined for any
  // workspace the daemon hasn't seen yet (just-added; system-
  // status hasn't included it yet) — those render without a
  // badge, which is the right "I don't know" signal.
  const byTeamID = new Map(
    systemStatus?.workspaces.map((w) => [w.team_id, w]) ?? [],
  );

  return (
    <aside
      data-testid="workspace-sidebar"
      className="w-16 bg-theme-bg-sidebar flex flex-col items-center py-4 gap-3 flex-shrink-0"
    >
      {workspaces.map((w) => {
        const isActive = w.team_id === activeWorkspaceID;
        const wsStatus = byTeamID.get(w.team_id);
        const color = wsStatus
          ? workspaceHealthColor({
              workspace: wsStatus,
              driveQueue: systemStatus?.drive_queue ?? null,
            })
          : null;
        const reason = wsStatus
          ? workspaceHealthReason({
              workspace: wsStatus,
              driveQueue: systemStatus?.drive_queue ?? null,
            })
          : null;

        return (
          <div key={w.team_id} className="relative group">
            <button
              type="button"
              onClick={() => onSelect(w.team_id)}
              data-testid={`workspace-icon-${w.team_id}`}
              data-active={isActive ? "true" : "false"}
              title={w.team_name}
              aria-label={`Select ${w.team_name}`}
              // Aurora: every workspace tile is a gradient bubble keyed
              // by name (same family as Home's workspace cards); the
              // active one gets a white ring + violet glow instead of
              // the old flat accent fill.
              style={{
                background: gradientCSSFor(w.team_name || w.team_id),
                boxShadow: isActive
                  ? "0 0 0 2px rgba(255,255,255,0.85), 0 4px 14px rgba(124,92,255,0.45)"
                  : undefined,
              }}
              className={[
                "w-10 h-10 flex items-center justify-center rounded-[13px]",
                "font-semibold text-xs tracking-wider text-white",
                "transition-all duration-150",
                isActive ? "" : "opacity-80 hover:opacity-100",
              ].join(" ")}
            >
              {getWorkspaceInitials(w.team_name)}
            </button>
            {color && (
              // Phase 5.0: clickable badge → /status. Anchored to
              // the bottom-right of the icon via absolute
              // positioning; the parent has `relative`. Pointer-
              // events are allowed (this is what differentiates
              // it from a pure decoration); the tooltip is the
              // native `title` attribute so it works without a
              // popper.
              <button
                type="button"
                onClick={() => navigate("/status")}
                data-testid={`workspace-health-badge-${w.team_id}`}
                data-color={color}
                title={`${w.team_name}: ${reason}`}
                aria-label={`${w.team_name} status: ${reason}. Open system status.`}
                className={[
                  "absolute -bottom-0.5 -right-0.5",
                  "w-2.5 h-2.5 rounded-full",
                  "ring-2 ring-theme-bg-sidebar",
                  BADGE_COLOR_CLASS[color],
                ].join(" ")}
              />
            )}
          </div>
        );
      })}

      {/* Phase 8.2: Slack-style "+" tile — self-service add-workspace
          from inside the browse rail. Opens the same web-login modal
          as the Chooser / status page; the backend enforces that a
          regular user signs in with their OWN Slack account. */}
      <button
        type="button"
        onClick={() => setShowAdd(true)}
        data-testid="workspace-add-tile"
        title="Add a workspace"
        aria-label="Add a workspace"
        className="w-10 h-10 rounded-[13px] flex items-center justify-center flex-shrink-0 border border-dashed border-theme-text-tertiary/50 text-theme-text-sidebar-icon hover:border-theme-accent-text hover:text-theme-accent-text hover:bg-theme-bg-sidebar-icon-hover transition-colors duration-150"
      >
        <Plus className="w-5 h-5" strokeWidth={1.75} aria-hidden />
      </button>

      {showAdd && <AddWorkspaceModal onClose={() => setShowAdd(false)} />}
    </aside>
  );
}
